import axios from 'axios'
import { BASE_URL } from '../constants/config'
import { getJwtToken, clearJwtTokenCache } from '../utils/jiraContext'
import { emitLicenseRequired } from '../utils/licenseManager'

/**
 * BUGS API
 *
 * API client for bug linking/unlinking operations on test case and test step executions.
 *
 * Endpoints:
 * - GET    /testscenarios/{id}/runs/{runId}/testcases/{testCaseId}/bugs     - List bugs for test case execution
 * - POST   /testscenarios/{id}/runs/{runId}/testcases/{testCaseId}/bugs     - Link bug to test case execution
 * - GET    /testscenarios/{id}/runs/{runId}/teststeps/{testStepId}/bugs     - List bugs for test step execution
 * - POST   /testscenarios/{id}/runs/{runId}/teststeps/{testStepId}/bugs     - Link bug to test step execution
 * - DELETE /testscenarios/{id}/runs/{runId}/bugs/{bugId}                     - Unlink bug from execution
 */

// Create axios instance for bugs API
const bugsClient = axios.create({
  baseURL: BASE_URL,
  headers: {
    'Content-Type': 'application/json'
  },
  timeout: 10000
})

/**
 * REQUEST INTERCEPTOR
 * Attaches JWT token to all requests.
 */
bugsClient.interceptors.request.use(
  async (config) => {
    try {
      const token = await getJwtToken()
      if (token) {
        config.headers.Authorization = `Bearer ${token}`
      }
    } catch (error) {
      console.error('Failed to get JWT token for bugs API:', error)
    }
    return config
  },
  (error) => Promise.reject(error)
)

/**
 * RESPONSE INTERCEPTOR
 * Handles authentication errors.
 */
bugsClient.interceptors.response.use(
  (response) => response,
  (error) => {
    console.error('Bugs API Error:', {
      url: error.config?.url,
      method: error.config?.method,
      status: error.response?.status,
      message: error.response?.data?.message || error.message
    })

    // Handle license validation failures - 402 means license is invalid/expired
    if (error.response?.status === 402) {
      emitLicenseRequired(error.response?.data?.message || 'License required')
    }

    if (error.response?.status === 401) {
      clearJwtTokenCache()
    }

    return Promise.reject(error)
  }
)

// ============================================================================
// TEST CASE EXECUTION BUGS
// ============================================================================

/**
 * GET TEST CASE EXECUTION BUGS
 *
 * Retrieves all bugs linked to a specific test case execution.
 *
 * @param {number|string} testScenarioId - The test scenario ID
 * @param {number|string} runId - The test run ID
 * @param {number|string} testCaseId - The test case ID
 * @returns {Promise<Array>} Array of bug objects
 */
export const getTestCaseExecutionBugs = async (testScenarioId, runId, testCaseId) => {
  try {
    const response = await bugsClient.get(
      `/testscenarios/${testScenarioId}/runs/${runId}/testcases/${testCaseId}/bugs`
    )
    return response.data
  } catch (error) {
    console.error('Error fetching test case execution bugs:', error)
    throw error
  }
}

/**
 * LINK BUG TO TEST CASE EXECUTION
 *
 * Links a Jira issue to a test case execution.
 *
 * @param {number|string} testScenarioId - The test scenario ID
 * @param {number|string} runId - The test run ID
 * @param {number|string} testCaseId - The test case ID
 * @param {string} jiraIssueKey - Jira issue key (e.g., "PROJ-123")
 * @returns {Promise<Object>} Created bug link object
 */
export const linkBugToTestCaseExecution = async (testScenarioId, runId, testCaseId, jiraIssueKey) => {
  try {
    const response = await bugsClient.post(
      `/testscenarios/${testScenarioId}/runs/${runId}/testcases/${testCaseId}/bugs`,
      { jiraIssueKey }
    )
    return response.data
  } catch (error) {
    console.error('Error linking bug to test case execution:', error)
    throw error
  }
}

// ============================================================================
// TEST STEP EXECUTION BUGS
// ============================================================================

/**
 * GET TEST STEP EXECUTION BUGS
 *
 * Retrieves all bugs linked to a specific test step execution.
 *
 * @param {number|string} testScenarioId - The test scenario ID
 * @param {number|string} runId - The test run ID
 * @param {number|string} testStepId - The test step ID
 * @returns {Promise<Array>} Array of bug objects
 */
export const getTestStepExecutionBugs = async (testScenarioId, runId, testStepId) => {
  try {
    const response = await bugsClient.get(
      `/testscenarios/${testScenarioId}/runs/${runId}/teststeps/${testStepId}/bugs`
    )
    return response.data
  } catch (error) {
    console.error('Error fetching test step execution bugs:', error)
    throw error
  }
}

/**
 * LINK BUG TO TEST STEP EXECUTION
 *
 * Links a Jira issue to a test step execution.
 *
 * @param {number|string} testScenarioId - The test scenario ID
 * @param {number|string} runId - The test run ID
 * @param {number|string} testStepId - The test step ID
 * @param {string} jiraIssueKey - Jira issue key (e.g., "PROJ-123")
 * @returns {Promise<Object>} Created bug link object
 */
export const linkBugToTestStepExecution = async (testScenarioId, runId, testStepId, jiraIssueKey) => {
  try {
    const response = await bugsClient.post(
      `/testscenarios/${testScenarioId}/runs/${runId}/teststeps/${testStepId}/bugs`,
      { jiraIssueKey }
    )
    return response.data
  } catch (error) {
    console.error('Error linking bug to test step execution:', error)
    throw error
  }
}

// ============================================================================
// SHARED OPERATIONS
// ============================================================================

/**
 * UNLINK BUG FROM EXECUTION
 *
 * Removes a bug link from an execution (test case or test step).
 * Any user can unlink a bug (no ownership restriction).
 *
 * @param {number|string} testScenarioId - The test scenario ID
 * @param {number|string} runId - The test run ID
 * @param {number|string} bugId - The bug link ID (not the Jira issue ID)
 * @returns {Promise<void>}
 */
export const unlinkBug = async (testScenarioId, runId, bugId) => {
  try {
    await bugsClient.delete(
      `/testscenarios/${testScenarioId}/runs/${runId}/bugs/${bugId}`
    )
  } catch (error) {
    console.error('Error unlinking bug:', error)
    throw error
  }
}

/**
 * GET ALL BUGS FOR TEST RUN
 *
 * Retrieves all bugs linked to any execution in a test run.
 *
 * @param {number|string} testScenarioId - The test scenario ID
 * @param {number|string} runId - The test run ID
 * @returns {Promise<Array>} Array of bug objects
 */
export const getTestRunBugs = async (testScenarioId, runId) => {
  try {
    const response = await bugsClient.get(
      `/testscenarios/${testScenarioId}/runs/${runId}/bugs`
    )
    return response.data
  } catch (error) {
    console.error('Error fetching test run bugs:', error)
    throw error
  }
}
